import { Transform } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayNotEmpty,
  ArrayUnique,
  IsArray,
  IsNotEmpty,
  IsOptional,
  IsString,
  MaxLength,
} from 'class-validator';
import { CONSULTANT_BULK_MAX_TENANTS } from './deadline-management.constants';

const DOCUMENT_TYPE_ID_MAX_LENGTH = 64;

export class DeadlineSameTypeSummaryQueryDto {
  @IsString()
  @IsNotEmpty()
  @MaxLength(DOCUMENT_TYPE_ID_MAX_LENGTH)
  documentTypeId!: string;

  /** Personal types (certificates, training): only the rows of this person count. */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  assigneeUserId?: string;
}

export class ConsultantDeadlineSameTypeSummaryQueryDto extends DeadlineSameTypeSummaryQueryDto {
  /** Comma-separated in the query string (`?tenantIds=a,b`), as many as one multi-client creation. */
  @Transform(({ value }) =>
    typeof value === 'string' ? value.split(',').filter((tenantId) => tenantId.length > 0) : value,
  )
  @IsArray()
  @ArrayNotEmpty()
  @ArrayMaxSize(CONSULTANT_BULK_MAX_TENANTS)
  @ArrayUnique()
  @IsString({ each: true })
  tenantIds!: string[];
}

/**
 * Deadlines a client already has for one document type: the creation dialogs show it as an
 * informative notice. Only clients with at least one row (active or archived) are listed.
 */
export interface DeadlineSameTypeSummaryDto {
  tenantId: string;
  /** Not archived, not deleted. */
  activeCount: number;
  archivedCount: number;
  /**
   * Row with the most recent file, otherwise the most recent row: among the active rows, or among
   * the archived ones when the client has no active row (`latestIsArchived`).
   */
  latestDeadlineId: string;
  latestHasDocument: boolean;
  latestIsArchived: boolean;
}

/** The tenant opens the file like from the archive, by document id. */
export interface TenantDeadlineSameTypeSummaryDto extends DeadlineSameTypeSummaryDto {
  latestDocumentId: string | null;
}

export interface ConsultantDeadlineSameTypeSummaryResponse {
  items: DeadlineSameTypeSummaryDto[];
}

export interface TenantDeadlineSameTypeSummaryResponse {
  items: TenantDeadlineSameTypeSummaryDto[];
}
